// Word-like editing of content lines, as pure functions so the preview's
// keyboard and paste handling can be tested without a browser. A "line" is
// one ContentLine (paragraph, bullet or sub-bullet); the caret is an index
// into a list plus a character offset in that line's text.

import { MAX_BULLET_LEVEL, lineLevel, lineMarker, type BulletMarker, type ContentLine, type EducationEntry, type LanguageEntry } from "./index.js";

export type LineKind = ContentLine["kind"];

export interface LineFocus {
  index: number;
  offset: number;
}

export interface LinesResult {
  lines: ContentLine[];
  focus: LineFocus;
}

/** A pasted line: kind null means "take the kind of the line pasted into". */
export interface PastedLine {
  text: string;
  kind: LineKind | null;
  level?: number;
  marker?: BulletMarker;
}

/** One level deeper: a paragraph becomes a level-0 list line, a list line
 *  goes down a level (up to five). The marker travels with it. */
export function indentLine(l: ContentLine): ContentLine {
  if (l.kind === "para") return { text: l.text, kind: "bullet", level: 0, marker: l.marker ?? "dot" };
  return { text: l.text, kind: "bullet", level: Math.min(MAX_BULLET_LEVEL, lineLevel(l) + 1), marker: lineMarker(l) };
}

/** One level back: a level-0 list line becomes a paragraph. */
export function outdentLine(l: ContentLine): ContentLine {
  if (l.kind === "para") return l;
  const level = lineLevel(l);
  if (level === 0) return { text: l.text, kind: "para" };
  return { text: l.text, kind: "bullet", level: level - 1, marker: lineMarker(l) };
}

const replaceAt = (lines: ContentLine[], index: number, line: ContentLine) =>
  lines.map((l, i) => (i === index ? line : l));

/** Enter: split the line at the caret; the new line keeps the kind so a
 *  sub-bullet continues as a sub-bullet. Enter on an EMPTY bullet outdents
 *  it instead, the way Word ends a list level. */
export function splitLineAt(lines: ContentLine[], index: number, offset: number): LinesResult {
  const line = lines[index]!;
  if (line.text === "" && line.kind !== "para") {
    return { lines: replaceAt(lines, index, outdentLine(line)), focus: { index, offset: 0 } };
  }
  const before = line.text.slice(0, offset);
  const after = line.text.slice(offset);
  const next = [...lines];
  next.splice(index, 1, { ...line, text: before }, { ...line, text: after });
  return { lines: next, focus: { index: index + 1, offset: 0 } };
}

/** Backspace at the start of a line: a list item loses one level first;
 *  a paragraph merges into the line above (caret at the join). Null when
 *  there is nothing to do (first paragraph). */
export function backspaceAtStart(lines: ContentLine[], index: number): LinesResult | null {
  const line = lines[index]!;
  if (line.kind !== "para") {
    return { lines: replaceAt(lines, index, outdentLine(line)), focus: { index, offset: 0 } };
  }
  if (index === 0) return null;
  return mergeInto(lines, index - 1);
}

/** Delete at the end of a line: the next line joins this one. */
export function deleteAtEnd(lines: ContentLine[], index: number): LinesResult | null {
  if (index >= lines.length - 1) return null;
  return mergeInto(lines, index);
}

function mergeInto(lines: ContentLine[], index: number): LinesResult {
  const keep = lines[index]!;
  const gone = lines[index + 1]!;
  const next = [...lines];
  next.splice(index, 2, { ...keep, text: keep.text + gone.text });
  return { lines: next, focus: { index, offset: keep.text.length } };
}

export function setLine(lines: ContentLine[], index: number, line: ContentLine, offset: number): LinesResult {
  return { lines: replaceAt(lines, index, line), focus: { index, offset } };
}

/** The marker style a pasted row's symbol stands for. */
export function markerOfSymbol(symbol: string): BulletMarker {
  if (/^[➢➤►▶>]$/.test(symbol)) return "arrow";
  if (/^[-–—]$/.test(symbol)) return "dash";
  if (/^[◦○]$/.test(symbol)) return "circle";
  if (/^[▪▫■□]$/.test(symbol)) return "square";
  if (/^\d{1,2}[.)]$/.test(symbol)) return "number";
  if (/^[a-z][.)]$/.test(symbol)) return "letter";
  return "dot";
}

/** Plain-text paste: one line per row; "•", "◦", "▪", "➢", "-", "・", "*",
 *  "1." and "a." markers become list lines of that style, every two spaces
 *  (or a tab) of indent one level deeper, anything else inherits the kind
 *  of the line it lands in. */
export function parsePastedText(text: string): PastedLine[] {
  const rows = text.replace(/\r\n?/g, "\n").split("\n");
  while (rows.length > 0 && rows[rows.length - 1]!.trim() === "") rows.pop();
  const marker = /^(\s*)([•◦‣▪▫●○■□·\-–—*・➢➤►▶>]|\d{1,2}[.)]|[a-z][.)])\s+/;
  return rows.map((row) => {
    const m = row.match(marker);
    if (!m) return { text: row.trim(), kind: null };
    const indent = m[1]!.replace(/\t/g, "  ").length;
    const level = Math.min(MAX_BULLET_LEVEL, Math.floor(indent / 2));
    return { text: row.slice(m[0].length).trim(), kind: "bullet", level, marker: markerOfSymbol(m[2]!) };
  });
}

/** Insert pasted lines at the caret: the first joins the current line, the
 *  rest follow as their own lines, and whatever came after the caret ends
 *  up after the last pasted line. */
export function pasteLines(
  lines: ContentLine[],
  index: number,
  offset: number,
  pasted: PastedLine[],
): LinesResult {
  if (pasted.length === 0) return { lines, focus: { index, offset } };
  const line = lines[index]!;
  const before = line.text.slice(0, offset);
  const after = line.text.slice(offset);
  const first = pasted[0]!;
  const asLine = (p: PastedLine, text: string): ContentLine =>
    p.kind === null || p.kind === "para"
      ? { ...line, text }
      : { text, kind: "bullet", level: p.level ?? 0, marker: p.marker ?? "dot" };
  const inserted: ContentLine[] = [line.text === "" && first.kind ? asLine(first, before + first.text) : { ...line, text: before + first.text }];
  for (const p of pasted.slice(1)) inserted.push(asLine(p, p.text));
  const last = inserted[inserted.length - 1]!;
  const focusOffset = last.text.length;
  last.text += after;
  const next = [...lines];
  next.splice(index, 1, ...inserted);
  return { lines: next, focus: { index: index + inserted.length - 1, offset: focusOffset } };
}

// ---- composite lines the preview shows as one string ------------------------
// Each is the exact inverse of how the renderer joins the fields, so an
// untouched line round-trips without loss.

export function splitEducationLine(
  text: string,
): Pick<EducationEntry, "qualification" | "field" | "honours"> {
  const [qualField = "", ...rest] = text.split(" — ");
  const honours = rest.length > 0 ? rest.join(" — ") : null;
  const comma = qualField.indexOf(", ");
  const qualification = comma >= 0 ? qualField.slice(0, comma) : qualField;
  const field = comma >= 0 ? qualField.slice(comma + 2) : null;
  return {
    qualification: qualification.trim() || null,
    field: field?.trim() || null,
    honours: honours?.trim() || null,
  };
}

export function splitCertificationLine(text: string): {
  name: string | null;
  issuer: string | null;
  year: number | null;
} {
  const parts = text.split(" — ").map((p) => p.trim());
  const last = parts[parts.length - 1] ?? "";
  const year = parts.length > 1 && /^\d{4}$/.test(last) ? Number(last) : null;
  if (year != null) parts.pop();
  return { name: parts[0] || null, issuer: parts.slice(1).join(" — ") || null, year };
}

export function parseLanguagesLine(
  text: string,
  format: "dash" | "paren" | undefined,
): LanguageEntry[] {
  return text
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const m =
        format === "paren"
          ? part.match(/^(.*?)\s*\((.*)\)$/)
          : part.match(/^(.*?)\s+[–-]\s+(.*)$/);
      return {
        language: (m ? m[1]! : part).trim(),
        statedLevel: m ? m[2]!.trim() || null : null,
        confidence: 1,
      };
    });
}

// ---- immutable updates by path ----------------------------------------------

export type PathSegment = string | number;

export function getAtPath(obj: unknown, path: PathSegment[]): unknown {
  let cur: unknown = obj;
  for (const seg of path) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[String(seg)];
  }
  return cur;
}

export function setAtPath<T>(obj: T, path: PathSegment[], value: unknown): T {
  if (path.length === 0) return value as T;
  const [head, ...rest] = path;
  if (Array.isArray(obj)) {
    const next = [...obj];
    next[Number(head)] = setAtPath(obj[Number(head)], rest, value);
    return next as T;
  }
  const record = (obj ?? {}) as Record<string, unknown>;
  return { ...record, [String(head)]: setAtPath(record[String(head)], rest, value) } as T;
}
